/**
 * SharePoint Service
 *
 * Service layer for interacting with Microsoft Graph API to manage SharePoint files and folders.
 */

import { sanitizeFolderName } from '../../utils/sanitizeFolderName';
import { logUploadDebug } from '../../utils/uploadDebug';
import { shouldUseMockSharePoint } from '../utils/sharePointDriveUtils';
import { telemetry } from '@/lib/telemetry';
import { MicrosoftGraphApiClient } from './graphApiClient';

/**
 * Signature for the per-upload progress callback exposed by
 * {@link SharePointService.uploadFile}.
 */
export type UploadProgressCallback = (progress: {
  bytesUploaded: number;
  totalBytes: number;
}) => void;

/**
 * Interface representing a SharePoint item (file or folder)
 */
export interface SharePointItem {
  id: string;
  name: string;
  webUrl: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  size?: number;
  folder?: {
    childCount: number;
  };
  file?: {
    mimeType: string;
  };
}

export interface SharePointPermission {
  email: string;
  roles: string[];
  id?: string; // Permission ID, used when revoking access
}

export type SharePointErrorCategory =
  | 'itemNotFound'
  | 'itemAlreadyExists'
  | 'other';

/**
 * Microsoft Graph's simple `PUT /content` upload endpoint rejects bodies
 * larger than 4 MiB. Above this threshold we must use a resumable upload
 * session instead.
 *
 * Docs: https://learn.microsoft.com/en-us/graph/api/driveitem-put-content
 */
const SIMPLE_UPLOAD_MAX_BYTES = 4 * 1024 * 1024;

/**
 * Size of each chunk sent to an upload session.
 *
 * Microsoft Graph requires every chunk except the final one to be a multiple
 * of 320 KiB (327,680 bytes), and caps a single PUT at less than 60 MiB.
 * 50 MiB (= 160 × 320 KiB) sits safely under that ceiling and minimizes
 * per-request overhead for large files; small files still take the simple
 * `PUT /content` path and never hit this constant.
 *
 * Docs: https://learn.microsoft.com/en-us/graph/api/driveitem-createuploadsession
 */
const UPLOAD_SESSION_CHUNK_BYTES = 50 * 1024 * 1024;

export class SharePointError extends Error {
  constructor(
    message: string,
    public readonly category: SharePointErrorCategory,
    public readonly httpStatus?: number,
    public readonly requestPath?: string,
  ) {
    super(message);
    this.name = 'SharePointError';
  }
}

/**
 * SharePoint Service class to interact with Microsoft Graph API
 */
export class SharePointService {
  private client: MicrosoftGraphApiClient;
  private driveId: string;

  // Retry configuration
  maxRetries: number = 5;
  initialRetryDelay: number = 2000;
  minRetryDelay: number = 5000;
  verboseLogging: boolean = process.env.NODE_ENV === 'development';

  // Add request queue to prevent too many concurrent requests
  private requestQueue: Array<() => Promise<any>> = [];
  private isProcessingQueue = false;
  private lastRequestTime = 0;
  private minTimeBetweenRequests = 500; // Minimum 500ms between requests

  constructor(client: MicrosoftGraphApiClient, driveId: string) {
    this.client = client;

    // Only validate driveId format in production
    if (!shouldUseMockSharePoint) {
      // Validate the driveId format - Microsoft drive IDs typically follow a specific format
      if (!driveId || typeof driveId !== 'string' || driveId.length < 8) {
        telemetry.error({
          error: new Error('Invalid SharePoint drive ID format'),
          message: 'Invalid SharePoint drive ID format detected.',
          extra: { driveId },
        });
        throw new Error('Invalid SharePoint drive ID format');
      }
    }

    this.driveId = driveId;
  }

  /**
   * Get a list of items in a folder
   */
  async listFolderContents(folderId: string): Promise<SharePointItem[]> {
    try {
      const response = await this.#enqueueGraphRequest({
        path:
          folderId === 'root' ? 'root/children' : `items/${folderId}/children`,
        method: 'GET',
      });
      const data = await response.json();

      return data.value.map((item: any) => ({
        id: item.id,
        name: item.name,
        webUrl: item.webUrl,
        folder: item.folder,
        file: item.file,
        createdDateTime: item.createdDateTime,
        lastModifiedDateTime: item.lastModifiedDateTime,
        size: item.size,
        parentReference: item.parentReference,
      }));
    } catch (error) {
      telemetry.error({
        error,
        message: 'Error listing folder contents.',
        extra: { driveId: this.driveId, folderId },
      });
      throw error;
    }
  }

  /**
   * Get a folder by path (relative to the root)
   */
  async getFolder(path: string): Promise<SharePointItem | undefined> {
    try {
      const response = await this.#enqueueGraphRequest({
        path: `root:/${path}`,
        method: 'GET',
      });
      const data = await response.json();

      return {
        id: data.id,
        name: data.name,
        webUrl: data.webUrl,
        folder: data.folder,
        file: data.file,
        createdDateTime: data.createdDateTime,
        lastModifiedDateTime: data.lastModifiedDateTime,
        size: data.size,
      };
    } catch (error) {
      if (
        error instanceof SharePointError &&
        error.category === 'itemNotFound'
      ) {
        return undefined;
      }
      throw error;
    }
  }

  /**
   * Look up a direct child of a folder by exact name.
   *
   * Uses the path-relative Graph endpoint
   * (`/drives/{driveId}/items/{parentId}:/{childName}`), which is
   * strongly consistent — unlike `searchFolderContents`, the result
   * reflects a freshly-created child immediately.
   *
   * Returns `undefined` if no child with that name exists.
   */
  async getChildByName(
    parentFolderId: string,
    childName: string,
  ): Promise<SharePointItem | undefined> {
    const encodedChildName = encodeURIComponent(childName);
    try {
      const response = await this.#enqueueGraphRequest({
        path:
          parentFolderId === 'root'
            ? `root:/${encodedChildName}`
            : `items/${parentFolderId}:/${encodedChildName}`,
        method: 'GET',
      });
      const data = await response.json();

      return {
        id: data.id,
        name: data.name,
        webUrl: data.webUrl,
        folder: data.folder,
        file: data.file,
        createdDateTime: data.createdDateTime,
        lastModifiedDateTime: data.lastModifiedDateTime,
        size: data.size,
      };
    } catch (error) {
      if (
        error instanceof SharePointError &&
        error.category === 'itemNotFound'
      ) {
        return undefined;
      }
      throw error;
    }
  }

  /**
   * Search for items in a folder
   */
  async searchFolderContents(
    folderId: string,
    searchQuery: string,
  ): Promise<SharePointItem[]> {
    try {
      const response = await this.#enqueueGraphRequest({
        path:
          folderId === 'root'
            ? `root/search(q='${searchQuery}')`
            : `items/${folderId}/search(q='${searchQuery}')`,
        method: 'GET',
      });
      const data = await response.json();

      return data.value.map((item: any) => ({
        id: item.id,
        name: item.name,
        webUrl: item.webUrl,
        folder: item.folder,
        file: item.file,
        createdDateTime: item.createdDateTime,
        lastModifiedDateTime: item.lastModifiedDateTime,
        size: item.size,
        parentReference: item.parentReference,
      }));
    } catch (error) {
      telemetry.error({
        error,
        message: 'Error searching folder contents.',
        extra: { driveId: this.driveId, folderId, searchQuery },
      });
      throw error;
    }
  }

  async createFolder({
    parentFolderId,
    name,
    conflictBehavior = 'fail',
  }: {
    parentFolderId: string;
    name: string;
    conflictBehavior?: 'replace' | 'fail' | 'rename';
  }): Promise<SharePointItem> {
    // Sanitize the folder name for SharePoint
    const sanitizedName = sanitizeFolderName(name);
    try {
      const body = JSON.stringify({
        name: sanitizedName,
        folder: {},
        '@microsoft.graph.conflictBehavior': conflictBehavior,
      });

      const response = await this.#enqueueGraphRequest({
        path:
          parentFolderId === 'root'
            ? 'root/children'
            : `items/${parentFolderId}/children`,
        contentType: 'application/json',
        method: 'POST',
        body,
      });
      const data = await response.json();
      return data;
    } catch (error) {
      telemetry.error({
        error,
        message: 'Error creating folder.',
        extra: { driveId: this.driveId, parentFolderId, name: sanitizedName },
      });
      throw error;
    }
  }

  /**
   * Upload a file to a folder.
   *
   * Files up to {@link SIMPLE_UPLOAD_MAX_BYTES} go through Microsoft Graph's
   * simple `PUT /content` endpoint. Larger files use a resumable upload
   * session (`createUploadSession` + chunked `PUT`s) because `PUT /content`
   * rejects bodies above that size.
   *
   * `onProgress`, when provided, is invoked with cumulative bytes uploaded at
   * the start and after each chunk completes, so callers can drive a
   * determinate progress bar. For small simple uploads only the start/end
   * bookends fire (the underlying `fetch` does not expose intermediate
   * progress).
   */
  async uploadFile(
    folderId: string,
    file: File,
    onProgress?: UploadProgressCallback,
  ): Promise<SharePointItem> {
    logUploadDebug('sharepoint', 'uploadFile: start', {
      folderId,
      fileName: file.name,
      fileSize: file.size,
      path:
        file.size <= SIMPLE_UPLOAD_MAX_BYTES ? 'simpleUpload' : 'chunkedUpload',
    });
    onProgress?.({ bytesUploaded: 0, totalBytes: file.size });
    try {
      if (file.size <= SIMPLE_UPLOAD_MAX_BYTES) {
        const item = await this.#simpleUpload(folderId, file);
        onProgress?.({ bytesUploaded: file.size, totalBytes: file.size });
        logUploadDebug('sharepoint', 'uploadFile: complete', {
          fileName: file.name,
          fileSize: file.size,
          path: 'simpleUpload',
          itemId: item.id,
        });
        return item;
      }
      const item = await this.#chunkedUpload(folderId, file, onProgress);
      logUploadDebug('sharepoint', 'uploadFile: complete', {
        fileName: file.name,
        fileSize: file.size,
        path: 'chunkedUpload',
        itemId: item.id,
      });
      return item;
    } catch (error) {
      telemetry.error({
        error,
        message: 'Error uploading file.',
        extra: {
          driveId: this.driveId,
          folderId,
          fileSize: file.size,
          fileName: file.name,
        },
      });
      throw error;
    }
  }

  /**
   * Get metadata for a specific folder
   */
  async getFolderMetadata(folderId: string): Promise<SharePointItem> {
    try {
      const response = await this.#enqueueGraphRequest({
        path: folderId === 'root' ? 'root' : `items/${folderId}`,
        method: 'GET',
      });
      const data = await response.json();
      return {
        id: data.id,
        name: data.name,
        webUrl: data.webUrl,
        folder: data.folder,
        file: data.file,
        createdDateTime: data.createdDateTime,
        lastModifiedDateTime: data.lastModifiedDateTime,
        size: data.size,
      };
    } catch (error) {
      telemetry.error({
        error,
        message: 'Error getting folder metadata.',
        extra: { driveId: this.driveId, folderId },
      });
      throw error;
    }
  }

  /**
   * Delete a file or folder
   */
  async deleteItem(itemId: string): Promise<void> {
    try {
      await this.#enqueueGraphRequest({
        path: `items/${itemId}`,
        method: 'DELETE',
      });
    } catch (error) {
      telemetry.error({
        error,
        message: 'Error deleting item.',
        extra: { driveId: this.driveId, itemId },
      });
      throw error;
    }
  }

  /**
   * Download a file
   */
  async downloadFile(fileId: string): Promise<Blob> {
    try {
      const response = await this.#enqueueGraphRequest({
        path: `items/${fileId}/content`,
        method: 'GET',
      });
      return await response.blob();
    } catch (error) {
      telemetry.error({
        error,
        message: 'Error downloading file.',
        extra: { driveId: this.driveId, fileId },
      });
      throw error;
    }
  }

  async createLink(props: {
    itemId: string;
    scope: 'organization' | 'anonymous';
  }): Promise<string> {
    const { itemId, scope } = props;

    const body: Record<string, any> = {
      type: 'view',
      scope,
    };
    if (scope === 'anonymous') {
      body.password = '';
      body.expirationDateTime = '';
    }

    const response = await this.#enqueueGraphRequest({
      path: `items/${itemId}/createLink`,
      contentType: 'application/json',
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!data.link?.webUrl) {
      throw new Error('No link was returned from the API');
    }
    return data.link.webUrl;
  }

  async sendInvite(props: {
    itemId: string;
    recipientEmailAddresses: string[];
    role: 'read' | 'write';
  }): Promise<void> {
    const { itemId, recipientEmailAddresses, role } = props;
    await this.#enqueueGraphRequest({
      path: `items/${itemId}/invite`,
      contentType: 'application/json',
      method: 'POST',
      body: JSON.stringify({
        requiresSignIn: true,
        recipients: recipientEmailAddresses.map((email) => ({
          email: email,
        })),
        roles: [role],
        sendInvitation: true,
        message:
          'You have been given access to shared documents. Please click the link to view or edit the shared content.',
      }),
    });
  }

  async listPermissions(props: {
    itemId: string;
  }): Promise<SharePointPermission[]> {
    const { itemId } = props;
    const response = await this.#enqueueGraphRequest({
      path: `items/${itemId}/permissions`,
      method: 'GET',
    });
    const data = await response.json();
    return data.value.map((permission: any) => ({
      email: permission.grantedTo.email,
      roles: permission.roles,
      id: permission.id,
    }));
  }

  async revokePermissions(props: { itemId: string; permissionId: string }) {
    const { itemId, permissionId } = props;
    await this.#enqueueGraphRequest({
      path: `items/${itemId}/permissions/${permissionId}`,
      method: 'DELETE',
    });
  }

  // ------------------------------------------------------------
  // Private methods
  // ------------------------------------------------------------

  /**
   * Simple upload for files smaller than 4MB
   */
  async #simpleUpload(folderId: string, file: File): Promise<SharePointItem> {
    const encodedFileName = encodeURIComponent(file.name);
    const response = await this.#enqueueGraphRequest({
      path:
        folderId === 'root'
          ? `root:/${encodedFileName}:/content`
          : `items/${folderId}:/${encodedFileName}:/content`,
      method: 'PUT',
      body: file,
      // NOTE: Not including content-type header - it will be set automatically
      // based on the file.
    });

    return await response.json();
  }

  /**
   * Resumable upload for files larger than {@link SIMPLE_UPLOAD_MAX_BYTES}.
   *
   * Creates an upload session via the Graph API, then streams the file to
   * the session's `uploadUrl` as sequential chunks of up to
   * {@link UPLOAD_SESSION_CHUNK_BYTES}. The final chunk's response contains
   * the newly-created `DriveItem`.
   */
  async #chunkedUpload(
    folderId: string,
    file: File,
    onProgress?: UploadProgressCallback,
  ): Promise<SharePointItem> {
    const uploadUrl = await this.#createUploadSession(folderId, file.name);
    logUploadDebug('sharepoint', 'chunkedUpload: session created', {
      fileName: file.name,
      totalBytes: file.size,
      chunkBytes: UPLOAD_SESSION_CHUNK_BYTES,
    });

    const totalBytes = file.size;
    let start = 0;
    while (start < totalBytes) {
      const end = Math.min(start + UPLOAD_SESSION_CHUNK_BYTES, totalBytes);
      const chunk = file.slice(start, end);
      logUploadDebug('sharepoint', 'chunkedUpload: chunk start', {
        fileName: file.name,
        start,
        end,
        totalBytes,
      });
      const response = await this.#uploadChunk({
        uploadUrl,
        chunk,
        start,
        end,
        totalBytes,
      });
      onProgress?.({ bytesUploaded: end, totalBytes });
      logUploadDebug('sharepoint', 'chunkedUpload: chunk done', {
        fileName: file.name,
        bytesUploaded: end,
        totalBytes,
        status: response.status,
      });

      // The final chunk's response (HTTP 200/201) carries the completed
      // DriveItem. Intermediate chunks return 202 Accepted and no DriveItem.
      if (end === totalBytes) {
        return await response.json();
      }
      start = end;
    }

    // `uploadFile` routes files smaller than SIMPLE_UPLOAD_MAX_BYTES through
    // `#simpleUpload`, so `totalBytes` here is always > 0 and the loop above
    // must exit via the `end === totalBytes` return. This throw exists only
    // to satisfy the type checker's control-flow analysis.
    throw new Error(
      'Chunked upload ended without a final response. This should be unreachable.',
    );
  }

  /**
   * Ask Graph for a pre-authenticated upload URL for the given file name.
   *
   * `conflictBehavior: 'replace'` mirrors the behavior of
   * {@link #simpleUpload} (which relies on Graph's `PUT /content` default),
   * so that small and large uploads behave identically when the user uploads
   * a file whose name already exists in the folder.
   */
  async #createUploadSession(
    folderId: string,
    fileName: string,
  ): Promise<string> {
    // Encode the filename segment so reserved URL characters (`#`, `?`, `%`,
    // etc.) in the name aren't parsed as fragment/query syntax. Matches the
    // pattern used in `getChildByName`.
    const encodedFileName = encodeURIComponent(fileName);
    const response = await this.#enqueueGraphRequest({
      path:
        folderId === 'root'
          ? `root:/${encodedFileName}:/createUploadSession`
          : `items/${folderId}:/${encodedFileName}:/createUploadSession`,
      method: 'POST',
      contentType: 'application/json',
      body: JSON.stringify({
        item: {
          '@microsoft.graph.conflictBehavior': 'replace',
        },
      }),
    });
    const data = await response.json();
    if (typeof data.uploadUrl !== 'string' || data.uploadUrl.length === 0) {
      throw new Error(
        'Microsoft Graph did not return an uploadUrl when creating the upload session.',
      );
    }
    return data.uploadUrl;
  }

  /**
   * PUT a single chunk to the upload session URL.
   *
   * The `uploadUrl` returned by `createUploadSession` is pre-authenticated
   * and goes to a separate host, so we bypass the Graph API client (and its
   * request queue) and call `fetch` directly. Per Microsoft's docs, chunk
   * PUTs must NOT include an `Authorization` header — the URL carries its
   * own credentials.
   *
   * Retries on 429 (rate-limited) and on 5xx (transient server errors).
   * This is a wider retry set than {@link #executeGraphRequest}, which only
   * retries 429, because resumable uploads are specifically designed so the
   * client can re-send a failed chunk with the same Content-Range — the
   * server deduplicates by offset — so blind retry on transient 5xx is
   * safe and meaningfully improves reliability on large files.
   */
  async #uploadChunk(props: {
    uploadUrl: string;
    chunk: Blob;
    start: number;
    end: number;
    totalBytes: number;
  }): Promise<Response> {
    const { uploadUrl, chunk, start, end, totalBytes } = props;
    // Content-Range uses inclusive byte offsets: for a 100-byte file uploaded
    // in one chunk this is `bytes 0-99/100`, not `bytes 0-100/100`.
    const contentRange = `bytes ${start}-${end - 1}/${totalBytes}`;

    let retryCount = 0;
    let retryDelay = this.initialRetryDelay;

    while (true) {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Range': contentRange,
        },
        body: chunk,
      });

      if (response.ok) {
        return response;
      }

      const isRetryable = response.status === 429 || response.status >= 500;
      if (!isRetryable || retryCount >= this.maxRetries) {
        throw await makeSharePointErrorFromDriveResponse(response);
      }

      const retryAfter = response.headers.get('Retry-After');
      const waitTime = retryAfter
        ? parseInt(retryAfter, 10) * 1000
        : Math.max(retryDelay, this.minRetryDelay);
      logUploadDebug('sharepoint', 'chunkedUpload: retrying chunk', {
        contentRange,
        status: response.status,
        retryCount,
        waitTimeMs: waitTime,
      });
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      retryDelay = Math.max(retryDelay * 2, this.minRetryDelay);
      retryCount++;
    }
  }

  /**
   * Helper function to make Graph API requests with retries for rate limiting
   */
  async #enqueueGraphRequest(request: GraphRequest): Promise<SuccessResponse> {
    // Add request to queue and wait for it to be processed
    const requestId = shortId();
    return new Promise((resolve, reject) => {
      this.#logRequestProgress(requestId, 'Enqueuing', { request });

      this.requestQueue.push(async () => {
        try {
          const result = await this.#executeGraphRequest({
            request,
            requestId,
          });
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });

      // Process queue if not already processing
      if (!this.isProcessingQueue) {
        this.#processRequestQueue();
      }
    });
  }

  /**
   * Process the request queue with rate limiting
   */
  async #processRequestQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.#logVerbose('Request queue: Starting processing');

    this.isProcessingQueue = true;

    try {
      while (this.requestQueue.length > 0) {
        this.#logVerbose(
          `Request queue: Processing next request (length ${this.requestQueue.length})`,
        );

        // Ensure minimum time between requests
        const now = Date.now();
        const timeSinceLastRequest = now - this.lastRequestTime;

        const delayMs = this.minTimeBetweenRequests - timeSinceLastRequest;
        if (delayMs > 0) {
          this.#logVerbose(`Request queue: Waiting for ${delayMs}ms`);
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }

        // Get the next request from the queue
        const nextRequest = this.requestQueue.shift();
        if (nextRequest) {
          this.lastRequestTime = Date.now();
          await nextRequest();
        }
      }
    } finally {
      this.isProcessingQueue = false;
      this.#logVerbose('Request queue: Ending processing');
    }
  }

  /**
   * Method to execute the actual Graph API request with retries
   */
  async #executeGraphRequest(props: {
    request: GraphRequest;
    requestId: string;
  }): Promise<SuccessResponse> {
    const { request, requestId } = props;
    this.#logRequestProgress(requestId, 'Executing', { request });

    const path = `/drives/${this.driveId}/${request.path}`;

    let retryCount = 0;
    let retryDelay = this.initialRetryDelay;

    const headers: HeadersInit = {};
    if (request.contentType) {
      headers['Content-Type'] = request.contentType;
    }
    const options: RequestInit = {
      method: request.method,
      headers,
    };
    if (request.method === 'POST' || request.method === 'PUT') {
      options.body = request.body;
    }

    // Loop until we succeed or run out of retries
    while (true) {
      this.#logRequestProgress(requestId, `Attempt ${retryCount + 1}`, {
        path,
        options,
      });

      const response = await this.client.fetch(path, options);

      // If request was successful, return the result
      if (response.ok) {
        this.#logRequestProgress(
          requestId,
          `Attempt ${retryCount + 1} successful`,
          {
            response,
          },
        );
        return response;
      }

      if (response.status !== 429 || retryCount >= this.maxRetries) {
        // For non-rate limited errors or if we've used all retries
        const error = await makeSharePointErrorFromDriveResponse(
          response,
          request.path,
        );
        this.#logRequestProgress(
          requestId,
          `Attempt ${retryCount + 1} failed`,
          {
            status: response.status,
            statusText: response.statusText,
            error,
          },
        );
        throw error;
      }

      // Get retry-after header if available, or use our exponential backoff
      const retryAfter = response.headers.get('Retry-After');
      // Use a longer default wait time for rate limiting (minimum 5 seconds)
      const waitTime = retryAfter
        ? parseInt(retryAfter, 10) * 1000
        : Math.max(retryDelay, this.minRetryDelay);

      this.#logRequestProgress(
        requestId,
        `Rate limited by Graph API. Retrying after ${waitTime}ms`,
        {
          retryCount,
          retryDelay,
          waitTime,
        },
      );

      // Wait for the specified time
      await new Promise((resolve) => setTimeout(resolve, waitTime));

      // Increase retry delay exponentially for next attempt (if needed)
      retryDelay = Math.max(retryDelay * 2, this.minRetryDelay);
      retryCount++;
    }
  }

  #logVerbose(message: string, data?: any) {
    if (this.verboseLogging) {
      console.log(`[GRAPH_API] ${message}`, data);
    }
  }

  #logRequestProgress(requestId: string, message: string, data?: any) {
    if (this.verboseLogging) {
      console.log(`[GRAPH_API:${requestId}] ${message}`, data);
    }
  }
}

const makeSharePointErrorFromDriveResponse = async (
  response: Response,
  requestPath?: string,
) => {
  const errorData = (await response.json()) as {
    error: {
      code: string;
      message: string;
    };
  };
  let category: SharePointErrorCategory = 'other';
  if (errorData.error.code === 'itemNotFound') {
    category = 'itemNotFound';
  } else if (errorData.error.code === 'nameAlreadyExists') {
    category = 'itemAlreadyExists';
  }

  return new SharePointError(
    errorData.error.message,
    category,
    response.status,
    requestPath,
  );
};

type GraphRequest = {
  path: string;
  contentType?: string;
} & (
  | {
      method: 'GET';
    }
  | {
      method: 'POST';
      body: BodyInit;
    }
  | {
      method: 'PUT';
      body: BodyInit;
    }
  | {
      method: 'DELETE';
    }
);

type SuccessResponse = {
  json: () => Promise<any>;
  blob: () => Promise<Blob>;
};

const shortId = (size = 10): string => {
  let id = '';
  while (id.length < size) id += Math.random().toString(36).slice(2);
  return id.slice(0, size);
};
